import { ref, watch, type Ref } from 'vue';
import { useReportsStore, type Report } from '../store'; // Adjust path as necessary
import { fetchReportFromApi } from '../mocks/report-utils'; // Adjust path as necessary
import { API_BASE_URL } from '@/config'; // Adjust path as necessary
import { useRoute } from 'vue-router';

/**
 * Fetches and manages the state for a single report.
 * @param reportSlugRef Reactive ref containing the report slug.
 */
export function useReportLoader(reportSlugRef: Ref<string>) {
  const route = useRoute();
  const reportsStore = useReportsStore();

  const report = ref<Report | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  const loadReport = async (slug: string) => {
    if (!slug) {
      report.value = null;
      error.value = 'Report slug is missing.';
      loading.value = false;
      return;
    }

    loading.value = true;
    error.value = null;
    report.value = null; // Clear previous report

    const shouldFetchFromApi = route.query.fetchFromApi === 'true';

    try {
      if (shouldFetchFromApi) {
        console.log(`Fetching report '${slug}' directly from API...`);
        const result = await fetchReportFromApi(slug, API_BASE_URL);
        report.value = result.report;
        error.value = result.error;
        // if(result.report) {
        //   reportsStore.addReport(result.report); // Keep example but commented
        // }
      } else {
        console.log(`Attempting to load report '${slug}' from store...`);
        const reportData = reportsStore.reports.find((r) => r.slug === slug);
        if (reportData) {
          console.log(`Report '${slug}' found in store.`);
          report.value = reportData;
        } else {
          console.log(`Report '${slug}' not found in store, fetching from API...`);
          const result = await fetchReportFromApi(slug, API_BASE_URL);
          report.value = result.report;
          error.value = result.error;
          // if(result.report) {
          //  reportsStore.addReport(result.report); // Keep example but commented
          // }
        }
      }
      if (!report.value && !error.value) {
         error.value = `Report data for '${slug}' could not be loaded.`
      }
    } catch (err: any) {
      console.error(`Error loading report '${slug}':`, err);
      error.value = err.message || 'An unexpected error occurred while loading the report.';
      report.value = null;
    } finally {
      loading.value = false;
      console.log(`Finished loading attempt for report '${slug}'. Loading: ${loading.value}, Error: ${error.value}`);
    }
  };

  watch(
    reportSlugRef,
    (newSlug) => {
      loadReport(newSlug);
    },
    { immediate: true }
  );

  return {
    report,
    loading,
    error,
    reloadReport: () => loadReport(reportSlugRef.value),
  };
} 